import { useCallback, useEffect, useId, useState } from "react";

import { businessApi } from "../../../../services/api";
import {
  Button, Dialog, EmptyState, ErrorState, Field, Input, Money, Notice, Select, SkeletonRows, Surface, Textarea, plainError,
} from "../../../../components/ui";
import RequestDecisionDialog from "./RequestDecisionDialog";
import { formatClock, formatDay } from "../myHrText";
import {
  SCHEDULED_STATE_LABEL, describeScheduledInterval, describeScheduledPeriod, describeWeekdays,
 isLiveScheduled } from "../scheduledOvertimeText";

// Report 70: overtime the employer authorises in advance, repeating on weekdays of one weekly schedule. It is the same
// overtime as a single request (one approval rule, one payroll calculation) and is paid only for time actually worked
// inside it. The server decides every permission and date rule; this panel shows the state and asks for decisions.

const WEEKDAYS = [[1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"], [5, "Fri"], [6, "Sat"], [7, "Sun"]];

function addMonthsISO(dateKey, months) {
  const [year, month, day] = dateKey.split("-").map(Number);
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, last));
  target.setUTCDate(target.getUTCDate() - 1);
  return target.toISOString().slice(0, 10);
}

function todayISO() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function scheduleLabel(schedule) {
  const work = schedule.days.filter((day) => day.shiftName);
  const shifts = [...new Set(work.map((day) => `${day.shiftName} ${formatClock(day.startTime)} – ${formatClock(day.endTime)}`))];
  const range = schedule.effectiveUntil ? `${formatDay(schedule.effectiveFrom, { year: true })} – ${formatDay(schedule.effectiveUntil, { year: true })}` : `from ${formatDay(schedule.effectiveFrom, { year: true })}`;
  return `${describeWeekdays(work.map((day) => day.weekday))} · ${shifts.join(", ")} · ${range}`;
}

/** Propose (or, for an approver, authorise) scheduled overtime for one employee. */
export function ScheduleOvertimeDialog({ employees = [], canApprove = false, onClose, onSaved }) {
  const formId = useId();
  const [memberId, setMemberId] = useState("");
  const [schedules, setSchedules] = useState(null);
  const [scheduleId, setScheduleId] = useState("");
  const [weekdays, setWeekdays] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [breakStart, setBreakStart] = useState("");
  const [breakMinutes, setBreakMinutes] = useState("");
  const [from, setFrom] = useState(todayISO());
  const [until, setUntil] = useState(addMonthsISO(todayISO(), 3));
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState("");
  const [invalid, setInvalid] = useState({});

  useEffect(() => {
    setSchedules(null);
    setScheduleId("");
    if (!memberId) return undefined;
    const controller = new AbortController();
    businessApi.getScheduledOvertimeSchedules(memberId, { signal: controller.signal })
      .then((res) => {
        const list = res?.schedules || [];
        setSchedules(list);
        const usable = list.filter((item) => !item.longShift);
        if (usable.length === 1) setScheduleId(String(usable[0].id));
      })
      .catch(() => { if (!controller.signal.aborted) setSchedules([]); });
    return () => controller.abort();
  }, [memberId]);

  const schedule = (schedules || []).find((item) => String(item.id) === scheduleId) || null;
  // Release closure: a weekly schedule whose shift pays more than a normal day cannot carry new scheduled overtime.
  const longOnly = Boolean(schedules?.length) && schedules.every((item) => item.longShift);
  const longText = (item) => `${item.longShift.name} pays ${Math.floor(item.longShift.paidMinutes / 60)} h a day as regular time, more than an ordinary ${Math.floor((item.normalDayMinutes || 480) / 60)} h day, so overtime cannot be scheduled on top of it. First move the employee to a shift that records the unpaid meal break or ends within ${Math.floor((item.normalDayMinutes || 480) / 60)} h (Shifts › Employee schedules › Change weekly schedule), then schedule the overtime on that schedule.`;
  const workDays = schedule ? schedule.days.filter((day) => day.shiftName).map((day) => day.weekday) : [];
  useEffect(() => {
    setWeekdays(workDays);
    if (schedule && schedule.effectiveFrom > from) setFrom(schedule.effectiveFrom);
    // Only when the chosen schedule changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId]);

  const toggleDay = (day) => setWeekdays((list) => (list.includes(day) ? list.filter((item) => item !== day) : [...list, day]));

  const submit = async (event) => {
    event.preventDefault();
    if (pending) return;
    const problems = {};
    if (!memberId) problems.member = "Choose the employee.";
    if (!scheduleId) problems.schedule = "Choose the weekly schedule it belongs to.";
    if (!weekdays.length) problems.weekdays = "Choose at least one work day.";
    if (!startTime || !endTime || startTime === endTime) problems.time = "Give a start and an end time.";
    if (breakMinutes && (!breakStart || Number(breakMinutes) < 1 || Number(breakMinutes) > 240)) problems.break = "Give the break's start and 1 to 240 minutes, or leave both empty.";
    if (!from || !until || until < from) problems.dates = "The end date must be on or after the start date.";
    if (reason.trim().length < 5) problems.reason = "Say why it is needed (at least 5 characters).";
    setInvalid(problems);
    if (Object.keys(problems).length) return;
    setPending(true);
    setFailure("");
    try {
      const res = await businessApi.proposeScheduledOvertime({
        business_member_id: Number(memberId),
        shift_assignment_id: Number(scheduleId),
        weekdays: [...weekdays].sort((a, b) => a - b),
        start_time: startTime,
        end_time: endTime,
        break_start: breakMinutes ? breakStart : null,
        break_minutes: breakMinutes ? Number(breakMinutes) : 0,
        effective_from: from,
        effective_until: until,
        reason: reason.trim(),
      });
      onSaved?.(res?.authorization || null);
    } catch (error) {
      setFailure(plainError(error, "The scheduled overtime was not saved. Try again."));
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      size="md"
      onOpenChange={(open) => { if (!open && !pending) onClose(); }}
      title="Schedule overtime"
      description={canApprove
        ? "You can approve overtime, so this is approved when you save it. Only time actually worked inside it is paid."
        : "This waits for someone who can approve overtime. Only time actually worked inside it is paid."}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button variant="primary" type="submit" form={formId} loading={pending} disabled={longOnly}>{canApprove ? "Schedule and approve" : "Send for approval"}</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Employee" required error={invalid.member}>
          <Select value={memberId} onChange={(e) => setMemberId(e.target.value)}>
            <option value="">Choose an employee</option>
            {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
          </Select>
        </Field>
        {memberId ? (
          <Field
            label="Weekly schedule"
            required
            error={invalid.schedule}
            hint={schedules && !schedules.length ? "This employee has no current or upcoming weekly schedule. Give them one in Shifts first." : "Scheduled overtime belongs to one weekly schedule and ends with it."}
          >
            <Select value={scheduleId} onChange={(e) => setScheduleId(e.target.value)} disabled={!schedules?.length || longOnly}>
              <option value="">{schedules === null ? "Loading schedules…" : "Choose a weekly schedule"}</option>
              {(schedules || []).map((item) => <option key={item.id} value={item.id} disabled={Boolean(item.longShift)}>{scheduleLabel(item)}{item.longShift ? " · not available: shift over 8 paid hours" : ""}</option>)}
            </Select>
          </Field>
        ) : null}
        {longOnly ? (
          <p role="alert" className="rounded-control bg-danger-subtle p-2.5 text-meta text-danger" data-testid="scheduled-ot-long-shift">{longText(schedules[0])}</p>
        ) : null}
        {schedule ? (
          <fieldset>
            <legend className="text-label text-secondary">Days it repeats on *</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {WEEKDAYS.map(([day, label]) => {
                const work = workDays.includes(day);
                return (
                  <label key={day} className={`inline-flex min-h-touch items-center gap-2 rounded-control border border-default px-3 text-body-sm md:min-h-0 md:h-control ${work ? "text-foreground" : "text-tertiary"}`}>
                    <input type="checkbox" checked={weekdays.includes(day)} disabled={!work} onChange={() => toggleDay(day)} />
                    {label}{work ? "" : " (rest)"}
                  </label>
                );
              })}
            </div>
            {invalid.weekdays ? <p className="mt-1 text-meta text-danger">{invalid.weekdays}</p> : null}
          </fieldset>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="From" required error={invalid.time}>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </Field>
          <Field label="Until" required>
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </Field>
        </div>
        <p className="-mt-2 text-meta text-tertiary">Right before or after the shift, never during it. An end earlier than the start is the next morning.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Unpaid break starts" hint="Optional" error={invalid.break}>
            <Input type="time" value={breakStart} onChange={(e) => setBreakStart(e.target.value)} />
          </Field>
          <Field label="Break minutes" hint="Optional">
            <Input type="number" inputMode="numeric" min={1} max={240} value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First day" required error={invalid.dates}>
            <Input type="date" min={todayISO()} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Last day" required hint="At most 12 months; renew with a new approval.">
            <Input type="date" min={from} max={from ? addMonthsISO(from, 12) : undefined} value={until} onChange={(e) => setUntil(e.target.value)} />
          </Field>
        </div>
        <Field label="Why is it needed?" required error={invalid.reason} hint="The employee sees this. At least 5 characters.">
          <Textarea rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="For example: peak season evening packing." />
        </Field>
        {failure ? <Notice tone="danger" role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}

function ScheduledRow({ item, canApprove, myMemberId, onAct }) {
  const state = SCHEDULED_STATE_LABEL[item.state] || item.state;
  const cost = item.costEstimate;
  const mine = Number(item.proposedByMemberId) === Number(myMemberId);
  return (
    <li className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0 space-y-0.5">
        <p className="text-body-sm font-medium text-foreground">{item.memberName || "Employee"}</p>
        <p className="text-body-sm text-foreground">{describeScheduledInterval(item)}</p>
        <p className="text-meta text-secondary">
          {describeScheduledPeriod(item)}
          {item.exceptions?.length ? ` · not on ${item.exceptions.map((entry) => formatDay(entry.date)).join(", ")}` : ""}
        </p>
        <p className="text-meta text-tertiary">{item.reason}{item.proposedByName ? ` · proposed by ${item.proposedByName}` : ""}{item.decisionNote ? ` · ${item.decisionNote}` : ""}{item.revokeReason ? ` · ${item.revokeReason}` : ""}</p>
        {cost ? (
          <p className="text-meta text-secondary">
            Up to <Money value={cost.perDayCents} /> a day; up to <Money value={cost.remainingCents} /> for the {item.remainingDays} remaining day{item.remainingDays === 1 ? "" : "s"} (estimate; only time actually worked is paid).
          </p>
        ) : null}
        {item.status === "pending" && canApprove && item.cannotApprove ? (
          <p className="text-meta text-warning"><span className="font-medium">Can't be approved:</span> {item.cannotApprove}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">
        <span className="text-body-sm text-secondary">{state}</span>
        {item.status === "pending" && canApprove ? (
          <>
            {item.cannotApprove ? null : <Button size="sm" variant="primary" onClick={() => onAct("approve", item)}>Approve</Button>}
            <Button size="sm" variant={item.cannotApprove ? "primary" : "secondary"} onClick={() => onAct("reject", item)}>Reject</Button>
          </>
        ) : null}
        {item.status === "pending" && (mine || canApprove) ? (
          <Button size="sm" variant="quiet" onClick={() => onAct("withdraw", item)}>Withdraw</Button>
        ) : null}
        {isLiveScheduled(item) && canApprove ? (
          <>
            <Button size="sm" variant="secondary" onClick={() => onAct("exception", item)}>Skip a day</Button>
            <Button size="sm" variant="secondary" onClick={() => onAct("revoke", item)}>End early</Button>
          </>
        ) : null}
      </div>
    </li>
  );
}

function Group({ title, items, ...rest }) {
  if (!items.length) return null;
  return (
    <Surface className="p-0">
      <h3 className="border-b border-subtle px-4 py-3 text-panel-title text-foreground">{title} <span className="text-meta tabular-nums text-tertiary">{items.length}</span></h3>
      <ul className="divide-y divide-subtle">
        {items.map((item) => <ScheduledRow key={item.id} item={item} {...rest} />)}
      </ul>
    </Surface>
  );
}

export default function ScheduledOvertimePanel({ canApprove = false, myMemberId = null, reloadKey = 0, onNotice, onPendingCount }) {
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);
  const [acting, setActing] = useState(null);
  const [date, setDate] = useState("");
  const [localReload, setLocalReload] = useState(0);

  const load = useCallback(async (signal) => {
    try {
      const res = await businessApi.getScheduledOvertime({ status: "all" }, { signal });
      const list = res?.authorizations || [];
      setItems(list);
      setFailed(false);
      onPendingCount?.(list.filter((item) => item.status === "pending").length);
    } catch {
      if (!signal?.aborted) setFailed(true);
    }
  }, [onPendingCount]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, reloadKey, localReload]);

  const open = (action, item) => {
    setDate(action === "revoke" ? todayISO() : "");
    setActing({ action, item });
  };

  const submit = async ({ note, overrideReason }) => {
    const { action, item } = acting;
    const extra = overrideReason ? { override_reason: overrideReason } : {};
    if (action === "revoke") await businessApi.actOnScheduledOvertime(item.id, "revoke", { revoked_from: date, reason: note, ...extra });
    else if (action === "exception") await businessApi.actOnScheduledOvertime(item.id, "exceptions", { date, reason: note, ...extra });
    else await businessApi.actOnScheduledOvertime(item.id, action, { note: note || undefined, ...extra });
    setActing(null);
    setLocalReload((key) => key + 1);
    onNotice?.({
      approve: "Scheduled overtime approved. Days already worked inside it are recalculated; the employee sees it in My HR.",
      reject: "Scheduled overtime not approved. The employee sees your reason.",
      withdraw: "Scheduled overtime withdrawn.",
      revoke: `Scheduled overtime ends from ${formatDay(date, { year: true })}. Earlier days keep it.`,
      exception: `No scheduled overtime on ${formatDay(date, { year: true })}.`,
    }[action]);
  };

  if (failed) return <ErrorState title="Scheduled overtime could not be loaded" onRetry={() => setLocalReload((key) => key + 1)} />;
  if (!items) return <Surface><SkeletonRows rows={3} /></Surface>;

  const pending = items.filter((item) => item.status === "pending");
  const upcoming = items.filter((item) => item.state === "upcoming");
  const inForce = items.filter((item) => item.state === "in_force");
  const past = items.filter((item) => item.status !== "pending" && !isLiveScheduled(item));
  const rowProps = { canApprove, myMemberId, onAct: open };

  let dialog = null;
  if (acting) {
    const { action, item } = acting;
    const facts = [
      { label: "Employee", value: item.memberName || "Employee" },
      { label: "Overtime", value: describeScheduledInterval(item) },
      { label: "Period", value: describeScheduledPeriod(item) },
    ];
    const copy = {
      approve: { title: `Approve scheduled overtime for ${item.memberName || "this employee"}?`, confirm: "Approve overtime", consequence: "The employee is authorised to work this time on those days. Only time actually worked inside it is paid; days already worked are recalculated." },
      reject: { title: `Reject scheduled overtime for ${item.memberName || "this employee"}?`, confirm: "Reject overtime", consequence: "Nothing is authorised. Time worked later can still be approved day by day.", reason: { label: "Reason the employee sees", required: true } },
      withdraw: { title: "Withdraw this scheduled overtime?", confirm: "Withdraw", consequence: "The proposal is closed; nothing was approved.", reason: { label: "Reason", required: true } },
      revoke: { title: `End ${item.memberName || "this employee"}'s scheduled overtime early?`, confirm: "End overtime", consequence: "It no longer applies from the date you choose. Earlier days keep it, and payroll already with Finance is never changed.", reason: { label: "Reason the employee sees", required: true } },
      exception: { title: "Skip scheduled overtime on one day?", confirm: "Skip this day", consequence: "No scheduled overtime applies on that day; other days are unchanged.", reason: { label: "Reason", required: true } },
    }[action];
    dialog = (
      <RequestDecisionDialog
        title={copy.title}
        facts={facts}
        consequence={copy.consequence}
        confirmLabel={copy.confirm}
        tone={action === "revoke" || action === "reject" ? "danger" : "default"}
        reason={copy.reason || null}
        failureTitle="Not saved"
        onSubmit={submit}
        onClose={() => setActing(null)}
      >
        {action === "revoke" || action === "exception" ? (
          <Field label={action === "revoke" ? "No overtime from" : "Day"} required hint={action === "revoke" ? `Between ${formatDay(item.effectiveFrom, { year: true })} and ${formatDay(item.effectiveUntil, { year: true })}.` : undefined}>
            <Input type="date" required min={item.effectiveFrom} max={item.revokedFrom || item.effectiveUntil} value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        ) : null}
      </RequestDecisionDialog>
    );
  }

  return (
    <div className="space-y-4">
      {!pending.length && !inForce.length && !upcoming.length ? (
        <Surface><EmptyState title="No scheduled overtime" description="Overtime authorised in advance for regular days appears here, waiting for approval or in force." /></Surface>
      ) : null}
      <Group title="Waiting for approval" items={pending} {...rowProps} />
      <Group title="In force now" items={inForce} {...rowProps} />
      <Group title="Approved, starts later" items={upcoming} {...rowProps} />
      {past.length ? (
        <details className="group">
          <summary className="cursor-pointer text-body-sm font-medium text-secondary">Ended or not approved ({past.length})</summary>
          <div className="mt-3"><Group title="Ended or not approved" items={past} {...rowProps} /></div>
        </details>
      ) : null}
      {dialog}
    </div>
  );
}
